import { useMatches } from "react-router-dom";
import { HeroBackdrop } from "./HeroBackdrop";

type HeroHandle = { heroHeight?: string };

/**
 * Persistent hero video layer — rendered ONCE in AppShell, above the router
 * Outlet, so the looping <video> mounts a single time and keeps playing across
 * navigations.
 *
 * Why this exists: previously every video-hero route (home + each PageHero
 * route) rendered its own <HeroBackdrop>. React Router swaps the Outlet on
 * navigation, so the old route's <video> unmounted and a brand-new one mounted
 * — and a fresh <video> paints its poster (the Diamond Cat still) before it can
 * fetch/decode the loop, so the poster flashed on every navigation. Hoisting the
 * backdrop above the Outlet makes its identity stable: it never remounts, so it
 * never reflashes.
 *
 * Height + visibility derive synchronously from the matched route's `handle`
 * (see App.tsx) during render — never from an effect — so there is no frame of
 * a default state. Routes without a hero (release/live detail, private, access)
 * have no `heroHeight` handle and collapse the layer to height 0; the element
 * stays mounted (the video keeps its buffered source) so returning to a hero
 * route is instant with no refetch.
 *
 * Stacking: this sits at zIndex 0 inside AppShell.Main; route content is wrapped
 * at zIndex 1, so content always paints above the video. The layer is
 * height-bounded to the hero band, so content below the hero sits over the page
 * background exactly as before.
 */
export function PersistentHeroBackdrop() {
  const matches = useMatches();
  // Deepest matched route wins (leaf routes carry the handle; the "/" root
  // does not), so nested routes can override if they ever need to.
  const heroHeight = [...matches]
    .reverse()
    .map((m) => (m.handle as HeroHandle | undefined)?.heroHeight)
    .find((h): h is string => Boolean(h));
  const active = Boolean(heroHeight);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: active ? heroHeight : 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <HeroBackdrop />
    </div>
  );
}
